import { getRouteApi, useRouter } from "@tanstack/react-router";
import { useGetUmbracoDeliveryApiV2ContentFeedContentIdSuspense } from "#/_generated/umbraco/delivery-api/features/custom/custom.ts";
import type {
	ContentFeedItemDto,
	ContentFeedUmbBlockElementModel,
	ContentFeedUmbBlockSettingsElementModel,
} from "#/_generated/umbraco/delivery-api/models";
import { ContentFeed } from "#/_packages/umbrella/features/content-feed/components/content-feed";
import type { BlockItemProps } from "#/_packages/umbrella/integrations/umbraco/types";
import { getContentFeedContentByIdFilter } from "#/_packages/umbrella/integrations/umbraco/utils/block-utils.ts";
import {
	tryGetAnimationEffectType,
	tryGetAnimationScrollType,
	tryGetContentSortOrder,
	tryGetHeadingLevel,
	tryGetIntent,
	tryGetOrientation,
	tryGetReadWidthLength,
	tryGetVariant,
} from "#/_packages/umbrella/utils";

type ContentFeedBlockProps = BlockItemProps & {
	content: ContentFeedUmbBlockElementModel;
	settings: ContentFeedUmbBlockSettingsElementModel;
};

export default function ContentFeedUmbBlock({
	className,
	content,
	settings,
}: ContentFeedBlockProps) {
	const isPreview = !!useRouter().options.context.previewBlock;

	if (isPreview) {
		return (
			<Preview className={className} content={content} settings={settings} />
		);
	} else {
		return (
			<Default className={className} content={content} settings={settings} />
		);
	}
}

function Default({ className, content, settings }: ContentFeedBlockProps) {
	const contentFeedFilter = getContentFeedContentByIdFilter(
		content?.properties,
	);

	const { data } = useGetUmbracoDeliveryApiV2ContentFeedContentIdSuspense(
		content.id,
		contentFeedFilter,
	);

	return (
		<ContentFeedRenderer
			className={className}
			content={content}
			settings={settings}
			items={data?.data ?? []}
		/>
	);
}

function Preview({ className, content, settings }: ContentFeedBlockProps) {
	const loaderData = getRouteApi(
		"/_umbraco/_preview/umbraco/block-preview",
	).useLoaderData();

	const items = (loaderData?.data as ContentFeedItemDto[]) ?? [];

	return (
		<ContentFeedRenderer
			className={className}
			content={content}
			settings={settings}
			items={items}
		/>
	);
}

type ContentFeedRendererProps = ContentFeedBlockProps & {
	items: ContentFeedItemDto[];
};

export function ContentFeedRenderer({
	className,
	content,
	settings,
	items,
}: ContentFeedRendererProps) {
	return (
		<ContentFeed
			className={className}
			items={items}
			headingLevel={tryGetHeadingLevel(content.properties?.headingLevel)}
			intentColor={tryGetIntent(content.properties?.intentColor)}
			typeVariant={tryGetVariant(content.properties?.typeVariant)}
			orientation={tryGetOrientation(content.properties?.orientation)}
			imagePositionEnd={content.properties?.imagePositionEnd ?? false}
			hideHeading={content.properties?.hideHeading ?? false}
			hideIntro={content.properties?.hideIntro ?? false}
			hideImage={content.properties?.hideImage ?? false}
			hidePublishDate={content.properties?.hidePublishDate ?? false}
			sortBy={tryGetContentSortOrder(content.properties?.sortBy)}
			id={settings.properties?.generatedId ?? undefined}
			readWidthToggle={settings.properties?.readWidthToggle ?? false}
			readWidthLength={tryGetReadWidthLength(
				settings.properties?.readWidthLengthPicker,
			)}
			animationScrollType={tryGetAnimationScrollType(
				settings.properties?.animationScrollType,
			)}
			animationEffectType={tryGetAnimationEffectType(
				settings.properties?.animationEffectType,
			)}
			animationHoverType={settings.properties?.animationHoverType ?? []}
			applyAnimationToChildren={
				settings.properties?.animationApplyToChildren ?? false
			}
			staggerAnimation={settings.properties?.animationStagger ?? false}
		/>
	);
}
